import {
  ArrowDown,
  ArrowLeft,
  CalendarDays,
  Package,
  PackageCheck,
  Settings2,
  Sun,
  UserPlus,
} from 'lucide-react';

import { Badge } from 'src/components/ui/badge';
import { StatusBadge } from 'src/components/ui/status-badge';

// Concept diagrams shown inside tour step cards. A fresh account sees the tour
// over empty pages (no recommendations, no POs, no measured lead times yet) —
// these teach the *shape* of what will appear without injecting fake rows into
// real surfaces, where they could be mistaken for live data. The "Example" chip
// keeps that boundary explicit.
//
// The product-surface diagrams (recommendation, purchaseOrder, poDetail,
// planToDraft) are faithful MINIATURES of the real components — same primitives
// (Badge, StatusBadge), same anatomy, same labels and status colors as
// RecommendationRow, the PO list columns, and the PO detail page
// (order-details-toolbar + details table) — so what the tour shows is
// recognizably what the merchant will meet. Semantic status color is deliberate
// here: it is part of the product's visual language, and the same tokens keep
// both themes correct. Keep these in sync with the components they mirror when
// those surfaces change.

// The chip straddles the frame's top border instead of floating inside the
// content box — content can never collide with it, at any card width.
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mt-2 rounded-md border bg-muted/40 p-3">
      <span className="absolute -top-2 right-2 rounded border bg-popover px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        Example
      </span>
      {children}
    </div>
  );
}

// Miniature of RecommendationRow: type icon tile, severity badge + headline,
// affected-count subline, order-by badge. Two rows read as the ranked list.
function RecommendationIllustration() {
  return (
    <Frame>
      <div className="flex items-center gap-2">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <Package className="size-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Badge variant="destructiveSoft" className="shrink-0 px-1.5 py-px text-[10px]">
              High
            </Badge>
            <p className="min-w-0 truncate text-xs font-semibold text-foreground">
              Reorder from Nordic Supply
            </p>
          </div>
          {/* Order-by rides the subline — the compact card is too narrow for the
              real row's right-edge badge without truncating the headline. */}
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">3 products</span>
            <Badge variant="warningSoft" className="shrink-0 px-1.5 py-px text-[10px]">
              Order by Aug 12
            </Badge>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 border-t pt-2 opacity-60">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
          <UserPlus className="size-3.5 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Badge variant="slateSoft" className="shrink-0 px-1.5 py-px text-[10px]">
              Low
            </Badge>
            <p className="min-w-0 truncate text-xs font-semibold text-foreground">
              Assign a supplier
            </p>
          </div>
          <p className="mt-0.5 truncate text-[10px] text-muted-foreground">1 product</p>
        </div>
      </div>
    </Frame>
  );
}

function PlanToDraftIllustration() {
  return (
    <Frame>
      <div className="rounded-md border bg-background p-2">
        <p className="text-xs font-medium text-foreground">Order 40 × Trail Runner</p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          Covers demand through Sep 3 · $312 total
        </p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
            Qty 40
          </span>
          <span className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
            By Aug 12
          </span>
          <span className="rounded border px-1.5 py-0.5 text-[10px] text-muted-foreground">
            Nordic Supply
          </span>
        </div>
      </div>
      {/* The conversion affordance, styled like the real primary button. */}
      <div className="my-1.5 flex flex-col items-center gap-1">
        <ArrowDown className="h-3 w-3 text-muted-foreground" />
        <span className="rounded-md bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground shadow-xs">
          Create Draft PO ($312)
        </span>
      </div>
      <div className="rounded-md border bg-background p-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Purchase order — Draft
        </p>
        <div className="mt-1.5 space-y-1">
          <p className="text-xs font-medium text-foreground">Trail Runner × 40</p>
          <div className="h-1.5 w-2/3 rounded-full bg-muted-foreground/20" />
        </div>
      </div>
    </Frame>
  );
}

// Miniature of the PO list: identity column, real StatusBadge statuses, and the
// expected-arrival cell (date + week number). One order in flight, one done.
function PurchaseOrderIllustration() {
  return (
    <Frame>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-foreground">PO #1024</p>
          <p className="truncate text-[10px] text-muted-foreground">Nordic Supply</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <StatusBadge status="warning" size="sm">
            Open - Pending
          </StatusBadge>
          <span className="text-[10px] tabular-nums text-muted-foreground">
            Arrives Aug 24 · Week 35
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2 opacity-60">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-foreground">PO #1019</p>
          <p className="truncate text-[10px] text-muted-foreground">Baltic Trading</p>
        </div>
        <StatusBadge status="success" size="sm">
          Completed
        </StatusBadge>
      </div>
    </Frame>
  );
}

// Miniature of the PO DETAIL page (order-details-toolbar + details table): back
// arrow, title + StatusBadge, the created/expected-delivery lines, the real
// action row with Receive Items in its live success-outline style, and the
// details table's essential columns with one line item. Shows an actual
// purchase order and situates the receive action; the caption carries the
// learning payoff (measured vs quoted lead time).
function PoDetailIllustration() {
  return (
    <Frame>
      <div className="flex items-center gap-1.5">
        <ArrowLeft className="size-3 shrink-0 text-muted-foreground" />
        <p className="min-w-0 truncate text-xs font-semibold text-foreground">PO #1024</p>
        <StatusBadge status="warning" size="sm" className="shrink-0">
          Open - Pending
        </StatusBadge>
      </div>
      <p className="mt-1 truncate text-[10px] text-muted-foreground/70">
        Created: Aug 10, 2026 · Expected Delivery: Aug 24, 2026
      </p>
      <div className="mt-1.5 flex items-center gap-1.5">
        <span className="flex items-center gap-1 rounded-md border border-success/30 bg-background px-1.5 py-0.5 text-[10px] font-medium text-success">
          <PackageCheck className="size-3" />
          Receive Items
        </span>
        <span className="rounded-md border bg-background px-1.5 py-0.5 text-[10px] text-foreground">
          Export
        </span>
        <span className="rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
          Edit
        </span>
      </div>
      <div className="mt-2 overflow-hidden rounded-md border bg-background">
        <div className="grid grid-cols-[1fr_3rem_3.5rem_4rem] items-center gap-1 border-b bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground">
          <span className="truncate">Product Details</span>
          <span className="text-center">Ordered</span>
          <span className="text-center">Received</span>
          <span className="text-center">Remaining</span>
        </div>
        <div className="grid grid-cols-[1fr_3rem_3.5rem_4rem] items-center gap-1 px-2 py-1.5">
          <span className="min-w-0 truncate text-xs font-medium text-foreground">Trail Runner</span>
          <span className="text-center text-xs tabular-nums text-muted-foreground">40</span>
          <span className="text-center text-xs tabular-nums text-muted-foreground">0</span>
          <span className="text-center text-xs tabular-nums text-foreground">40</span>
        </div>
      </div>
      <p className="mt-2 border-t pt-2 text-[10px] leading-snug text-muted-foreground">
        When the delivery arrives, Receive Items records what came in and when. After three
        deliveries, Athory plans with the{' '}
        <span className="font-medium text-foreground">measured 11 d</span> lead time, not the quoted
        14 d.
      </p>
    </Frame>
  );
}

function ForecastProjectionIllustration() {
  return (
    <Frame>
      <div className="space-y-2.5">
        <div>
          <p className="text-[10px] text-muted-foreground">Forecast</p>
          <svg viewBox="0 0 200 48" className="mt-1 h-12 w-full" aria-hidden>
            <line
              x1="92"
              y1="4"
              x2="92"
              y2="44"
              strokeDasharray="2 3"
              strokeWidth="1"
              className="stroke-border"
            />
            <path d="M92,24 L200,6 L200,42 Z" className="fill-foreground/10" />
            <path
              d="M92,24 L200,24"
              fill="none"
              strokeWidth="1"
              strokeDasharray="2 3"
              className="stroke-foreground/40"
            />
            <path
              d="M0,30 L18,25 L36,32 L54,20 L72,27 L92,24"
              fill="none"
              strokeWidth="1.5"
              className="stroke-foreground"
            />
          </svg>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">Projected stock</p>
          <svg viewBox="0 0 200 48" className="mt-1 h-12 w-full" aria-hidden>
            <path
              d="M0,10 H52 V22 H92 V34 H120"
              fill="none"
              strokeWidth="1.5"
              className="stroke-foreground/70"
            />
            <path
              d="M120,34 V12 H200"
              fill="none"
              strokeWidth="1.5"
              className="stroke-foreground/70"
            />
            <circle cx="120" cy="34" r="2.5" className="fill-foreground" />
            {/* The svg is height-bound (h-12 = viewBox height), so user units render
                1:1 — 9 here matches the text-[9px] floor the Example chip uses. */}
            <text x="124" y="45" fontSize="9" className="fill-muted-foreground">
              PO arrives
            </text>
          </svg>
        </div>
      </div>
    </Frame>
  );
}

function RhythmIllustration() {
  const rows = [
    { Icon: Sun, label: 'Daily', description: 'Scan the Daily Overview' },
    {
      Icon: CalendarDays,
      label: 'Weekly',
      description: 'Act on recommendations, receive deliveries',
    },
    { Icon: Settings2, label: 'Once', description: 'Assign suppliers, add costs' },
  ];
  return (
    <div className="space-y-2">
      {rows.map(({ Icon, label, description }) => (
        <div key={label} className="flex items-center gap-2.5">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/40">
            <Icon className="size-3.5 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export const TOUR_ILLUSTRATIONS = {
  recommendation: RecommendationIllustration,
  planToDraft: PlanToDraftIllustration,
  purchaseOrder: PurchaseOrderIllustration,
  poDetail: PoDetailIllustration,
  forecastProjection: ForecastProjectionIllustration,
  rhythm: RhythmIllustration,
} as const;

export type TourIllustrationId = keyof typeof TOUR_ILLUSTRATIONS;

export function TourIllustration({ id }: { id: TourIllustrationId }) {
  const Illustration = TOUR_ILLUSTRATIONS[id];
  return <Illustration />;
}
